import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { Search, CheckCircle2, PauseCircle, XCircle, Filter, Building2, ShieldCheck } from "lucide-react";
import { ENTITY_CATEGORIES, STATUSES } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusBadge({ status }) {
  const map = {
    Active: { cls: "bg-[#eaf4ee] text-[#137a47]", Icon: CheckCircle2 },
    Suspended: { cls: "bg-amber-50 text-amber-700", Icon: PauseCircle },
    Revoked: { cls: "bg-red-50 text-red-700", Icon: XCircle },
  };
  const { cls, Icon } = map[status] || map.Active;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${cls}`}>
      <Icon size={13} /> {status}
    </span>
  );
}

export default function Entities() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All Categories");
  const [status, setStatus] = useState("All Statuses");
  const [entities, setEntities] = useState([]);

  useEffect(() => {
    axios.get(`${API}/entities`).then((res) => setEntities(res.data)).catch(() => setEntities([]));
  }, []);

  const results = useMemo(() => {
    return entities.filter((e) => {
      const matchQ = e.name.toLowerCase().includes(query.trim().toLowerCase());
      const matchC = category === "All Categories" || e.category === category;
      const matchS = status === "All Statuses" || e.status === status;
      return matchQ && matchC && matchS;
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [entities, query, category, status]);

  const activeCount = entities.filter((e) => e.status === "Active").length;
  const hasSearched =
    query.trim() !== "" || category !== "All Categories" || status !== "All Statuses";

  return (
    <div>
      {/* Page header */}
      <section className="hero-pattern text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-green bg-white/95 px-3 py-1.5 rounded-full mb-5">
            <ShieldCheck size={14} /> Public Register
          </span>
          <h1 className="font-serif text-3xl sm:text-5xl font-bold mb-4">Regulated Entities</h1>
          <p className="text-slate-200 max-w-2xl text-lg">
            Search the official register of firms licensed by the Commission. Always confirm a
            firm&apos;s name and that its status is <span className="font-semibold text-white">Active</span> before investing.
          </p>
        </div>
      </section>

      {/* Search panel */}
      <section className="bg-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-6 sm:p-8 -mt-20 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              <div className="lg:col-span-6">
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">Firm name</label>
                <div className="relative">
                  <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by company name..."
                    className="w-full pl-11 pr-4 py-3 rounded-lg border border-slate-300 focus:border-brand-green focus:ring-2 focus:ring-green-100 outline-none text-sm"
                  />
                </div>
              </div>
              <div className="lg:col-span-3">
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-3 rounded-lg border border-slate-300 focus:border-brand-green focus:ring-2 focus:ring-green-100 outline-none text-sm bg-white"
                >
                  {ENTITY_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="lg:col-span-3">
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-3 py-3 rounded-lg border border-slate-300 focus:border-brand-green focus:ring-2 focus:ring-green-100 outline-none text-sm bg-white"
                >
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-5 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1.5"><Building2 size={15} className="text-brand-navy" /> {entities.length} total registered</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={15} className="text-brand-green" /> {activeCount} active</span>
              {(query || category !== "All Categories" || status !== "All Statuses") && (
                <button
                  onClick={() => { setQuery(""); setCategory("All Categories"); setStatus("All Statuses"); }}
                  className="inline-flex items-center gap-1.5 text-brand-green font-semibold hover:underline ml-auto"
                >
                  <Filter size={14} /> Clear filters
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="bg-slate-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {!hasSearched ? (
            <div className="bg-white rounded-xl border border-slate-200 p-16 text-center">
              <Search size={40} className="mx-auto text-brand-green mb-4" />
              <h3 className="font-serif text-xl font-bold text-brand-navy mb-2">Search the register</h3>
              <p className="text-slate-500 text-sm max-w-md mx-auto">
                Enter a firm name above, or select a category or status, to view licensed
                entities. Results are shown only after you search.
              </p>
            </div>
          ) : results.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-16 text-center">
              <Search size={40} className="mx-auto text-slate-300 mb-4" />
              <h3 className="font-serif text-xl font-bold text-brand-navy mb-2">No matching entities found</h3>
              <p className="text-slate-500 text-sm">
                If you cannot find a firm, it may not be licensed. Contact the Commission to verify.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-500 mb-4">
                Showing <span className="font-semibold text-brand-navy">{results.length}</span> {results.length === 1 ? "entity" : "entities"}
              </p>
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                {/* Table head (desktop) */}
                <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3.5 bg-brand-navy text-white text-xs font-semibold uppercase tracking-wide">
                  <div className="col-span-6">Entity Name</div>
                  <div className="col-span-3">Category</div>
                  <div className="col-span-2">Licence No.</div>
                  <div className="col-span-1 text-right">Status</div>
                </div>
                <div className="divide-y divide-slate-100">
                  {results.map((e) => (
                    <div key={e.license} className="entity-row px-6 py-4 grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 md:items-center">
                      <div className="md:col-span-6 font-semibold text-brand-navy">{e.name}</div>
                      <div className="md:col-span-3 text-sm text-slate-500">{e.category}</div>
                      <div className="md:col-span-2 text-sm text-slate-400 font-mono">{e.license}</div>
                      <div className="md:col-span-1 md:text-right"><StatusBadge status={e.status} /></div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <p className="text-xs text-slate-400 mt-6 max-w-3xl">
            Disclaimer: This register reflects the licensing records of the Commission. A status of
            &quot;Active&quot; confirms a firm is currently authorised. &quot;Suspended&quot; or
            &quot;Revoked&quot; firms must not solicit or accept investments.
          </p>
        </div>
      </section>
    </div>
  );
}
